// sapi-admin/src/hooks/useNotificationSound.js
import { useRef, useEffect, useState } from 'react';

const SOUND_PATH = '/sounds/mario.mp3';

export function useNotificationSound(enabled = true, soundPath = SOUND_PATH) {
  const audioRef = useRef(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);

  // Charger l'audio et vérifier qu'il est prêt
  useEffect(() => {
    if (!enabled) return;

    const audio = new Audio(soundPath);
    
    // Vérifier que l'audio est chargé correctement
    audio.addEventListener('canplaythrough', () => {
      console.log('✅ Audio prêt à être joué');
      setIsAudioReady(true);
    });
    
    audio.addEventListener('error', (e) => {
    // Ignorer les erreurs de cleanup StrictMode (src vide ou relatif)
    if (!audioRef.current || !audio.src || audio.src === window.location.href) return;
    console.error('❌ Fichier audio introuvable:', soundPath);
    });
    
    audio.load();
    audioRef.current = audio;

    return () => {
        const a = audioRef.current;
        audioRef.current = null; // ← nullifier AVANT de modifier src
        if (a) {
            a.pause();
            a.src = '';
        }
        };
    }, [enabled, soundPath]);

  // Débloquer après interaction utilisateur
  useEffect(() => {
    if (!enabled) return;

    const handleUserInteraction = () => {
      if (audioRef.current && !userInteracted) {
        console.log('🖱️ Interaction - déblocage du son');
        // Tenter de jouer pour débloquer (mise en pause immédiate)
        audioRef.current.play()
          .then(() => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setUserInteracted(true);
            console.log('🔊 Son débloqué avec succès');
          })
          .catch(e => console.log('Déblocage échoué:', e.message));
      }
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [enabled, userInteracted]);

  const playSound = () => {
    if (!enabled) {
      console.log('🔇 Son désactivé');
      return;
    }
    if (!audioRef.current) {
      console.log('⚠️ Audio non chargé');
      return;
    }
    if (!isAudioReady) {
      console.log('⚠️ Audio pas encore prêt');
      return;
    }
    if (!userInteracted) {
      console.log('⚠️ Attente interaction utilisateur...');
      // Option: essayer quand même (certains navigateurs l'acceptent après un clic)
      return;
    }
    
    try {
      audioRef.current.currentTime = 0;
      const promise = audioRef.current.play();
      if (promise !== undefined) {
        promise
          .then(() => console.log('🔊 Son joué !'))
          .catch(e => console.warn('🔇 Erreur:', e.message));
      }
    } catch (error) {
      console.warn('🔇 Exception:', error);
    }
  };

  const testSound = () => {
    if (!userInteracted) {
      alert('Click anywhere on the page first, then test again.');
      return;
    }
    playSound();
  };

  return { playSound, testSound, userInteracted };
}